import PrintIcon from "@mui/icons-material/Print";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AsyncSearch, { IOption } from "../../components/asyncSearch/AsyncSearch";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { PageCard } from "../../components/pageCard/PageCard";
import { CustomMenuItem } from "../../components/table/CustomMenuItem/CustomMenuItem";
import { CustomNoContentTableBody } from "../../components/table/CustomNoContentTableBody/CustomNoContentTableBody";
import { CustomTableCell } from "../../components/table/CustomTableCell/CustomTableCell";
import CustomTablePagination from "../../components/table/CustomTablePagination/CustomTablePagination";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { ICollector } from "../../interfaces/ICollector";
import { IExhibition } from "../../interfaces/IExhibition";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import { openBase64Pdf } from "../../utils/downloadPdf";
import { getUtcOffset } from "../../utils/timezone";

export const Reception: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;
  const { exhibitionId } = useParams<{ exhibitionId: string }>();
  const navigate = useNavigate();
  const toastr = useToastr();

  const [loading, setLoading] = useState(false);
  const [selectedExhibition, setSelectedExhibition] =
    useState<IExhibition | null>(null);

  const [collectorOptions, setCollectorOptions] = useState<IOption[]>([]);
  const [selectedCollectorOption, setSelectedCollectorOption] =
    useState<IOption | null>(null);
  const [loadingCollectors, setLoadingCollectors] = useState(false);
  const collectorsMapRef = useRef<Record<string, ICollector>>({});

  const [plants, setPlants] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingPlants, setLoadingPlants] = useState(false);

  const [pendingReceived, setPendingReceived] = useState<Set<string>>(
    new Set(),
  );
  const [pendingDelivered, setPendingDelivered] = useState<Set<string>>(
    new Set(),
  );
  const [pendingEventsReport, setPendingEventsReport] = useState<Set<string>>(
    new Set(),
  );

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPlant, setEditingPlant] = useState<any | null>(null);
  const [editName, setEditName] = useState("");
  const [editGenealogy, setEditGenealogy] = useState("");
  const [loadingEdit, setLoadingEdit] = useState(false);

  useEffect(() => {
    async function loadExhibitionDetails() {
      if (!exhibitionId) {
        toastr.error("Nenhuma exposição informada");
        navigate("/exhibitions");
        return;
      }
      setLoading(true);
      try {
        const response = await api.get(`/exhibitions/${exhibitionId}`);
        setSelectedExhibition(response.data);
      } catch (err: any) {
        toastr.error(
          err.response?.data?.message || "Erro ao obter detalhes da exposição",
        );
        navigate("/exhibitions");
      } finally {
        setLoading(false);
      }
    }

    loadExhibitionDetails();
  }, [exhibitionId, navigate, toastr]);

  const handleSearchCollectors = useCallback(
    async (filterText: string = "") => {
      if (!exhibitionId) return;
      setLoadingCollectors(true);

      try {
        const response = await api.get("/collectors/dynamically", {
          params: {
            perPage: 100,
            currentPage: 1,
            orderBy: "name",
            orderDirection: "asc",
            filter: filterText,
            exhibitionId,
          },
        });

        const collectors = response.data.data || [];

        const options: IOption[] = collectors.map((collector: ICollector) => ({
          value: collector.id,
          description: [collector.name, collector.city, collector.association]
            .filter(Boolean)
            .join(" / "),
        }));

        const collectorMap: Record<string, ICollector> = {};
        collectors.forEach((collector: ICollector) => {
          collectorMap[collector.id] = collector;
        });

        collectorsMapRef.current = collectorMap;
        setCollectorOptions(options);
      } catch (err: any) {
        toastr.error(
          err.response?.data?.message || "Erro ao buscar colecionadores",
        );
        setCollectorOptions([]);
      } finally {
        setLoadingCollectors(false);
      }
    },
    [exhibitionId, toastr],
  );

  const handleSelectCollector = useCallback((option: IOption | null) => {
    if (option) {
      const collector = collectorsMapRef.current[option.value];
      if (collector) {
        setSelectedCollector(collector);
        setSelectedCollectorOption(option);
      }
    } else {
      setSelectedCollector(null);
      setSelectedCollectorOption(null);
    }
  }, []);

  const [selectedCollector, setSelectedCollector] = useState<ICollector | null>(
    null,
  );

  const handleFetchPlants = useCallback(
    async ({
      perPage,
      currentPage,
      collectorId,
    }: {
      perPage: number;
      currentPage: number;
      collectorId: string;
    }) => {
      if (!exhibitionId || !collectorId) return;
      setLoadingPlants(true);
      try {
        const response = await api.get(
          `/collector/plants/exhibition/${exhibitionId}/collector/${collectorId}`,
          {
            params: {
              perPage,
              currentPage: currentPage + 1,
              orderBy: "number",
              orderDirection: "asc",
            },
          },
        );
        if (response.data) {
          setPlants(response.data.data || []);
          setTotalRows(response.data.totalRows || 0);
        }
      } catch (err: any) {
        toastr.error(
          err.response?.data?.message ||
            "Erro ao carregar plantas do colecionador",
        );
        setPlants([]);
        setTotalRows(0);
      } finally {
        setLoadingPlants(false);
      }
    },
    [exhibitionId, toastr],
  );

  useEffect(() => {
    if (selectedCollector) {
      setCurrentPage(0);
      handleFetchPlants({
        perPage,
        currentPage: 0,
        collectorId: selectedCollector.id,
      });
    } else {
      setPlants([]);
      setTotalRows(0);
    }
  }, [selectedCollector, handleFetchPlants, perPage]);

  const handlePagination = async (
    newPerPage: number,
    newCurrentPage: number,
  ) => {
    setPerPage(newPerPage);
    setCurrentPage(newCurrentPage);
    if (selectedCollector) {
      handleFetchPlants({
        perPage: newPerPage,
        currentPage: newCurrentPage,
        collectorId: selectedCollector.id,
      });
    }
  };

  const handleToggleReceived = async (
    plantId: string,
    currentValue: boolean,
  ) => {
    if (pendingReceived.has(plantId)) return;
    const newValue = !currentValue;
    setPlants(prev =>
      prev.map(p => (p.id === plantId ? { ...p, received: newValue } : p)),
    );
    setPendingReceived(prev => {
      const next = new Set(prev);
      next.add(plantId);
      return next;
    });
    try {
      await api.patch("/collector/plants/received", [
        { id: plantId, received: newValue },
      ]);
    } catch (err: any) {
      setPlants(prev =>
        prev.map(p => (p.id === plantId ? { ...p, received: currentValue } : p)),
      );
      toastr.error(
        err.response?.data?.message || "Erro ao atualizar recebimento",
      );
    } finally {
      setPendingReceived(prev => {
        const next = new Set(prev);
        next.delete(plantId);
        return next;
      });
    }
  };

  const handleToggleDelivered = async (
    plantId: string,
    currentValue: boolean,
  ) => {
    if (pendingDelivered.has(plantId)) return;
    const newValue = !currentValue;
    setPlants(prev =>
      prev.map(p => (p.id === plantId ? { ...p, delivered: newValue } : p)),
    );
    setPendingDelivered(prev => {
      const next = new Set(prev);
      next.add(plantId);
      return next;
    });
    try {
      await api.patch("/collector/plants/delivered", [
        { id: plantId, delivered: newValue },
      ]);
    } catch (err: any) {
      setPlants(prev =>
        prev.map(p =>
          p.id === plantId ? { ...p, delivered: currentValue } : p,
        ),
      );
      toastr.error(
        err.response?.data?.message || "Erro ao atualizar entrega",
      );
    } finally {
      setPendingDelivered(prev => {
        const next = new Set(prev);
        next.delete(plantId);
        return next;
      });
    }
  };

  const handlePrintReport = async () => {
    if (!selectedCollector || !exhibitionId) return;
    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.write("<p>Gerando relatório... Aguarde.</p>");
    }
    setLoading(true);
    try {
      const response = await api.get(
        `/collector/plants/report/exhibition/${exhibitionId}/collector/${selectedCollector.id}`,
        {
          params: {
            onlyReceived: true,
            utcOffset: getUtcOffset(),
          },
        },
      );

      const fileData = response.data[0];
      if (!fileData || !fileData.base64) {
        toastr.error("Nenhum relatório encontrado para imprimir.");
        if (newWindow) newWindow.close();
        return;
      }

      openBase64Pdf(fileData.base64, fileData.filename || "relatorio.pdf", newWindow);
    } catch (error: any) {
      if (newWindow) newWindow.close();
      toastr.error(
        error.response?.data?.message ||
          "Erro ao gerar relatório de impressão.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = useCallback(
    (id: string) => {
      const plant = plants.find(p => p.id === id);
      if (!plant) return;
      setEditingPlant(plant);
      setEditName(plant.name || "");
      setEditGenealogy(plant.genealogy || "");
      setEditDialogOpen(true);
    },
    [plants],
  );

  const handleEventsReport = async (plantId: string) => {
    if (pendingEventsReport.has(plantId)) return;
    setPendingEventsReport(prev => {
      const next = new Set(prev);
      next.add(plantId);
      return next;
    });
    try {
      const response = await api.get(
        `/collector/plants/${plantId}/events/report`,
        { params: { utcOffset: getUtcOffset() } },
      );
      const { filename, base64 } = response.data;
      const base64Clean = base64.replace(/^data:application\/pdf;base64,/, "");
      const byteCharacters = atob(base64Clean);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename || "historico_eventos.pdf";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toastr.error(
        err.response?.data?.message || "Erro ao gerar histórico de eventos",
      );
    } finally {
      setPendingEventsReport(prev => {
        const next = new Set(prev);
        next.delete(plantId);
        return next;
      });
    }
  };

  const handleCloseEdit = () => {
    if (loadingEdit) return;
    setEditDialogOpen(false);
    setEditingPlant(null);
    setEditName("");
    setEditGenealogy("");
  };

  const isEditNameEmpty = editName.trim() === "";
  const isEditUnchanged = editingPlant
    ? editName === (editingPlant.name || "") &&
      editGenealogy === (editingPlant.genealogy || "")
    : true;

  const handleSaveEdit = async () => {
    if (!editingPlant || isEditNameEmpty || isEditUnchanged) return;
    setLoadingEdit(true);
    try {
      const body: Record<string, any> = {};
      if (editName !== (editingPlant.name || "")) body.name = editName;
      if (editGenealogy !== (editingPlant.genealogy || "")) {
        body.genealogy = editGenealogy || null;
      }

      await api.patch(`/collector/plants/${editingPlant.id}`, body);
      toastr.success("Planta atualizada com sucesso");
      handleCloseEdit();
      if (selectedCollector) {
        await handleFetchPlants({
          perPage,
          currentPage,
          collectorId: selectedCollector.id,
        });
      }
    } catch (err: any) {
      toastr.error(err.response?.data?.message || "Erro ao atualizar planta");
    } finally {
      setLoadingEdit(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <TitleContainer title="Mesa de Recepção de Plantas" />
        <Button
          variant="contained"
          color="inherit"
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Voltar
        </Button>
      </Box>

      {loading && <BackdropCustom />}

      {selectedExhibition && (
        <PageCard>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Exposição
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedExhibition.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Data
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedExhibition.date
                  ? new Date(selectedExhibition.date).toLocaleDateString(
                      "pt-BR",
                    )
                  : "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Presidente
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedExhibition.president || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Coordenador
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedExhibition.coordinator || "—"}
              </Typography>
            </Grid>
          </Grid>
        </PageCard>
      )}

      {selectedExhibition && !selectedExhibition.allowRegistration && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          O recebimento de plantas está desabilitado para esta exposição.
        </Alert>
      )}

      {selectedExhibition && (
        <PageCard>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={8} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Pesquisar Colecionador
              </Typography>
              <AsyncSearch
                label="Buscar colecionador"
                options={collectorOptions}
                asyncSearch={handleSearchCollectors}
                setOptions={setCollectorOptions}
                setOption={handleSelectCollector}
                loading={loadingCollectors}
                option={selectedCollectorOption}
                disabled={!selectedExhibition.allowRegistration}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={handlePrintReport}
                disabled={
                  !selectedCollector || !selectedExhibition.allowRegistration
                }
                startIcon={<PrintIcon />}
                size="medium"
                sx={{ height: "40px" }}
              >
                Imprimir Relatório
              </Button>
            </Grid>
          </Grid>
        </PageCard>
      )}

      {selectedCollector && (
        <PageCard>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Colecionador
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedCollector.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Cidade
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedCollector.city || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                Associação
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedCollector.association || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="textSecondary">
                E-mail
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {selectedCollector.email || "—"}
              </Typography>
            </Grid>
          </Grid>
        </PageCard>
      )}

      {selectedCollector && (
        <PageCard>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Plantas do Colecionador</span>
          </Typography>

          {loadingPlants ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ minHeight: "300px" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          borderBottom: 1,
                          borderColor: "#E5E5E5",
                        },
                      }}
                    >
                      <TableCell size="small" width={60}>
                        <strong>Ações</strong>
                      </TableCell>
                      <TableCell size="small" width={80}>
                        <strong>Nº</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Nome / Genealogia</strong>
                      </TableCell>
                      <TableCell size="small" width={100} align="center">
                        <strong>Recebido</strong>
                      </TableCell>
                      <TableCell size="small" width={100} align="center">
                        <strong>Entregue</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plants.map(plant => {
                      return (
                        <TableRow
                          key={plant.id}
                          hover
                          sx={{
                            "&:last-child td, &:last-child th": {
                              border: 0,
                            },
                          }}
                        >
                          <CustomMenuItem
                            id={plant.id}
                            handleToEdit={handleOpenEdit}
                            handleToEventsReport={handleEventsReport}
                            disableButton={pendingEventsReport.has(plant.id)}
                          />
                          <CustomTableCell size="small" width={80}>
                            {plant.number}
                          </CustomTableCell>
                          <TableCell size="small">
                            <Typography variant="body2">
                              {plant.name}
                            </Typography>
                            {plant.genealogy && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                              >
                                {plant.genealogy}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" size="small" width={100}>
                            {pendingReceived.has(plant.id) ? (
                              <CircularProgress size={24} />
                            ) : (
                              <Checkbox
                                checked={!!plant.received}
                                color="primary"
                                onChange={() =>
                                  handleToggleReceived(
                                    plant.id,
                                    !!plant.received,
                                  )
                                }
                              />
                            )}
                          </TableCell>
                          <TableCell align="center" size="small" width={100}>
                            {pendingDelivered.has(plant.id) ? (
                              <CircularProgress size={24} />
                            ) : (
                              <Checkbox
                                checked={!!plant.delivered}
                                color="primary"
                                onChange={() =>
                                  handleToggleDelivered(
                                    plant.id,
                                    !!plant.delivered,
                                  )
                                }
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {plants.length === 0 && (
                <CustomNoContentTableBody description="Nenhuma planta encontrada" />
              )}

              {plants.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <CustomTablePagination
                    totalRows={totalRows}
                    currentPage={currentPage}
                    perPage={perPage}
                    handleRequest={handlePagination}
                    setPerPage={setPerPage}
                    setCurrentPage={setCurrentPage}
                    orderField="number"
                    orderDirection="asc"
                  />
                </Box>
              )}
            </>
          )}
        </PageCard>
      )}

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .MuiDialog-container": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          },
        }}
      >
        <DialogTitle>Editar Planta</DialogTitle>
        <DialogContent>
          <TextField
            label="Nome"
            value={editName}
            onChange={e => setEditName(e.target.value)}
            fullWidth
            required
            error={isEditNameEmpty}
            helperText={isEditNameEmpty ? "Nome é obrigatório" : ""}
            margin="normal"
            disabled={loadingEdit}
            size="small"
          />
          <TextField
            label="Genealogia"
            value={editGenealogy}
            onChange={e => setEditGenealogy(e.target.value)}
            fullWidth
            margin="normal"
            disabled={loadingEdit}
            required={true}
            size="small"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={loadingEdit}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={loadingEdit || isEditNameEmpty || isEditUnchanged}
            startIcon={
              loadingEdit ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
